import { createAsyncThunk } from '@reduxjs/toolkit';
import type {
  CreateOrUpdatePaymentMethodPayPalInput,
  AddMemberToPartnerInput, VerifyMemberPaypalIdentityInput, ShoppingStatus,
  SaveCompanyPartnerRelationInput,
  CreateOrUpdatePaymentMethodACHInput,
} from '@optimus/models';
import * as dashboardAPI from '../../api/dashboardAPI';
import {
  PaymentMethodType, PaymentMethod, PayPalAccount, BankAccount,
} from '../../common/models/PaymentMethod';
import { RootState } from '../storeSetup';
import { MemberConfig } from '../../common/models/Member';
import { Logger } from '../../config/logging/logger';
import { Partner } from '../../common/models/Partner';
import { PartnerProfileConfig } from '../../common/models/PartnerProfile';
import { Category } from '../../common/models/Category';
import { PartnerEnrollmentStatus } from '../../common/enums';
import { asArrayPayload } from './asArrayPayload';

export interface ResponseError {
  // TODO: improve this interface to account for expected errors from API call
  errorMessage?: string
}

export interface MemberPartnerRelation {
  id: string;
  partnerId: string;
  partnerMemberKey?: string;
  partnerCodeEnteredInMobile?: boolean;
  partnerMemberEmail?: string;
  shoppingStatus?: ShoppingStatus | null;
  enrollmentStatus: PartnerEnrollmentStatus | null;
}

export interface CompanyPartnerRelation {
  id: string;
  companyId: string;
  partnerId: string,
  partnerMemberKey?: string | null,
  partnerMemberEmail?: string | null,
  partnerCodeEnteredInMobile?: boolean,
  partnerMemberKeyForgotten?: boolean,
  shoppingStatus?: ShoppingStatus | null,
  enrollmentSource?: string | null,
  partnerName?: string | null;
  enrollmentFormSubmittedAt?: string | null;
  enrollmentCompletedAt?: string | null;
  enrollmentStatus?: PartnerEnrollmentStatus | null;
  enrolledDistributorOrManufacturerId?: string | null
  createdDate: string
}

export interface YearlyPayments {
  year: number;
  total: string;
}

export interface CompanyWalletTransactions {
  id: string;
  type: string;
  partner: string;
  createdAt: string;
  amount: string;
  status: string;
  payoutMethod?: string;
}

export interface CompanyWallet {
  balance: string;
  totalRewards: number;
  totalPayouts: number;
  transactions: CompanyWalletTransactions[];
}

export interface CompanyRewardsWallet {
  balance: string;
  totalRewards: number;
  transactions: CompanyWalletTransactions[];
}

export interface CompanyPayoutsWallet {
  totalPayouts: number;
  transactions: CompanyWalletTransactions[];
}

export const getAllActivePartners = createAsyncThunk<
// Return type of the payload creator
Partner[],
// First argument to the payload creator
string | undefined,
// Types for ThunkAPI
{
  rejectValue: ResponseError;
}>(
  'dashboard/getAllActivePartners',
  async (membershipType, { rejectWithValue }) => {
    const logger = Logger.create('dashboard/getAllActivePartners');
    try {
      const response = await dashboardAPI.getAllActivePartners(membershipType);
      return asArrayPayload<Partner>(response, 'active partners', logger);
    } catch (err) {
      logger.error(Logger.format(err));
      const responseError: ResponseError = { errorMessage: 'failed to get all active partners' };
      return rejectWithValue(responseError as ResponseError);
    }
  },
);

export const getRecentRebatePartners = createAsyncThunk<
// Return type of the payload creator
Partner[],
// First argument to the payload creator
{ memberId: string },
// Types for ThunkAPI
{
  rejectValue: ResponseError;
}>(
  'dashboard/getRecentRebatePartners',
  async ({ memberId }, { rejectWithValue }) => {
    const logger = Logger.create('dashboard/getRecentRebatePartners');
    try {
      const response = await dashboardAPI.getRecentRebatePartners(memberId);
      return asArrayPayload<Partner>(response, 'recent rebate partners', logger);
    } catch (err) {
      logger.error(Logger.format(err));
      const responseError: ResponseError = { errorMessage: 'failed to get recent rebate partners' };
      return rejectWithValue(responseError as ResponseError);
    }
  },
);

export const getPaymentMethods = createAsyncThunk<
// Return type of the payload creator
PaymentMethod[],
// First argument to the payload creator
undefined,
// Types for ThunkAPI
{
  rejectValue: ResponseError;
  state: RootState
}>(
  'dashboard/getPaymentMethods',
  async (_, { rejectWithValue, getState }) => {
    const logger = Logger.create('dashboard/getPaymentMethods');
    const { memberSession: { memberId } } = getState().onboarding;
    try {
      const response = await dashboardAPI.getPaymentMethods(memberId);
      return asArrayPayload<PaymentMethod>(response, 'payment methods', logger);
    } catch (err) {
      logger.error(Logger.format(err));
      const responseError: ResponseError = { errorMessage: 'failed to get payment methods' };
      return rejectWithValue(responseError as ResponseError);
    }
  },
);

// currently used to add bank account payment method only
// to add paypal payment method, use verifyAndUpdatePaypalEmail
export const setPaymentMethod = createAsyncThunk<
// Return type of the payload creator
BankAccount,
// First argument to the payload creator
CreateOrUpdatePaymentMethodPayPalInput | CreateOrUpdatePaymentMethodACHInput,
// Types for ThunkAPI
{
  rejectValue: ResponseError;
  state: RootState
}>(
  'dashboard/setPaymentMethod',
  async (request, { rejectWithValue, getState }) => {
    const logger = Logger.create('dashboard/setPaymentMethod');
    const { memberSession: { memberId } } = getState().onboarding;
    try {
      const paymentMethod: PaymentMethod = await dashboardAPI
        .setPaymentMethod(memberId, request);
      const bankAccount = paymentMethod.type === PaymentMethodType.BANK_ACCOUNT ?
        paymentMethod : undefined;
      return bankAccount as BankAccount;
    } catch (err) {
      logger.error(Logger.format(err));
      const responseError: ResponseError = { errorMessage: 'something went wrong' };
      return rejectWithValue(responseError as ResponseError);
    }
  },
);

export const getLennoxMemberRelation = createAsyncThunk<
// Return type of the payload creator
MemberPartnerRelation,
// First argument to the payload creator
undefined,
// Types for ThunkAPI
{
  rejectValue: ResponseError;
  state: RootState
}>(
  'dashboard/getLennoxMemberRelation',
  async (_, { rejectWithValue, getState }) => {
    const logger = Logger.create('dashboard/getLennoxMemberRelation');
    const { memberSession: { memberId } } = getState().onboarding;
    const lennoxPartnerId = process.env.REACT_APP_LENNOX_PARTNER_ID || '';
    try {
      const lennoxMemberRelation = await dashboardAPI.getLennoxMemberRelation(
        memberId,
        lennoxPartnerId,
      );
      return (lennoxMemberRelation || undefined) as MemberPartnerRelation;
    } catch (err) {
      logger.error(Logger.format(err));
      const responseError: ResponseError = { errorMessage: 'failed to get lennoxMemberRelation' };
      return rejectWithValue(responseError as ResponseError);
    }
  },
);

export const getHomeDepotMemberRelation = createAsyncThunk<
// Return type of the payload creator
MemberPartnerRelation,
// First argument to the payload creator
undefined,
// Types for ThunkAPI
{
  rejectValue: ResponseError;
  state: RootState
}>(
  'dashboard/getHomeDepotMemberRelation',
  async (_, { rejectWithValue, getState }) => {
    const logger = Logger.create('dashboard/getHomeDepotMemberRelation');
    const { memberSession: { memberId } } = getState().onboarding;
    const hdPartnerId = process.env.REACT_APP_HOME_DEPOT_PARTNER_ID || '';
    try {
      const hdMemberRelation = await dashboardAPI.getHomeDepotMemberRelation(
        memberId,
        hdPartnerId,
      );
      return (hdMemberRelation || undefined) as MemberPartnerRelation;
    } catch (err) {
      logger.error(Logger.format(err));
      const responseError: ResponseError = { errorMessage: 'failed to get homeDepotMemberRelation' };
      return rejectWithValue(responseError as ResponseError);
    }
  },
);

export const getCompanyPartnerRelations = createAsyncThunk<
// Return type of the payload creator
CompanyPartnerRelation[],
// First argument to the payload creator
string,
// Types for ThunkAPI
{
  rejectValue: ResponseError;
}>(
  'dashboard/getCompanyPartnerRelations',
  async (companyId, { rejectWithValue }) => {
    const logger = Logger.create('dashboard/getCompanyPartnerRelations');
    try {
      const companyPartnerRelations = await dashboardAPI.getCompanyPartnerRelations(companyId);
      return asArrayPayload<CompanyPartnerRelation>(
        companyPartnerRelations,
        'companyPartnerRelations',
        logger,
      );
    } catch (err) {
      logger.error(Logger.format(err));
      const responseError: ResponseError = { errorMessage: 'failed to get companyPartnerRelations' };
      return rejectWithValue(responseError as ResponseError);
    }
  },
);

export const updateCompanyPartnerRelation = createAsyncThunk<
// Return type of the payload creator
CompanyPartnerRelation,
// First argument to the payload creator
SaveCompanyPartnerRelationInput,
// Types for ThunkAPI
{
  rejectValue: ResponseError;
  state: RootState
}>(
  'dashboard/updateCompanyPartnerRelation',
  async (request, { rejectWithValue }) => {
    const logger = Logger.create('dashboard/updateCompanyPartnerRelation');
    try {
      const cp = await dashboardAPI.updateCompanyPartnerRelations(
        request,
      );
      return cp as CompanyPartnerRelation;
    } catch (err) {
      logger.error(Logger.format(err));
      const responseError: ResponseError = { errorMessage: 'failed to update companyPartnerRelation' };
      return rejectWithValue(responseError as ResponseError);
    }
  },
);

export const enrollCompanyToPartner = createAsyncThunk<
CompanyPartnerRelation,
{
  companyId: string;
  partnerId: string;
  data: dashboardAPI.PartnerEnrollRequest;
},
{
  rejectValue: ResponseError;
}
>(
  'dashboard/enrollCompanyToPartner',
  async (request, { rejectWithValue }) => {
    const logger = Logger.create('dashboard/enrollCompanyToPartner');
    try {
      const relation = await dashboardAPI.enrollCompanyToPartner(
        request.companyId,
        request.partnerId,
        request.data,
      );
      return relation as CompanyPartnerRelation;
    } catch (err) {
      logger.error(Logger.format(err));
      const responseError: ResponseError = { errorMessage: 'failed to enroll company to partner' };
      return rejectWithValue(responseError);
    }
  },
);

export const unlinkCompanyFromPartner = createAsyncThunk<
CompanyPartnerRelation,
{
  companyId: string;
  partnerId: string;
},
{
  rejectValue: ResponseError;
}
>(
  'dashboard/unlinkCompanyFromPartner',
  async (request, { rejectWithValue }) => {
    const logger = Logger.create('dashboard/unlinkCompanyFromPartner');
    try {
      const relation = await dashboardAPI.unlinkCompanyFromPartner(
        request.companyId,
        request.partnerId,
      );
      return relation as CompanyPartnerRelation;
    } catch (err) {
      logger.error(Logger.format(err));
      const responseError: ResponseError = { errorMessage: 'failed to unlink company from partner' };
      return rejectWithValue(responseError);
    }
  },
);

export const updateCompanyPartner = createAsyncThunk<
CompanyPartnerRelation,
{
  companyId: string;
  partnerId: string;
  data: dashboardAPI.UpdateCompanyPartnerRequest;
},
{
  rejectValue: ResponseError;
}
>(
  'dashboard/updateCompanyPartner',
  async (request, { rejectWithValue }) => {
    const logger = Logger.create('dashboard/updateCompanyPartner');
    try {
      const relation = await dashboardAPI.updateCompanyPartner(
        request.companyId,
        request.partnerId,
        request.data,
      );
      return relation as CompanyPartnerRelation;
    } catch (err) {
      logger.error(Logger.format(err));
      const responseError: ResponseError = { errorMessage: 'failed to update company partner' };
      return rejectWithValue(responseError);
    }
  },
);

export const setLennoxMemberKey = createAsyncThunk<
// Return type of the payload creator
MemberPartnerRelation,
// First argument to the payload creator
{ memberId: string, partnerId: string, data: AddMemberToPartnerInput },
// Types for ThunkAPI
{
  rejectValue: ResponseError;
}>(
  'dashboard/setLennoxMemberKey',
  async (request, { rejectWithValue }) => {
    const logger = Logger.create('dashboard/setLennoxMemberKey');
    try {
      const lennoxMemberRelation = await dashboardAPI
        .setLennoxMemberKey(request.memberId, request.partnerId, request.data);
      return (lennoxMemberRelation || undefined) as MemberPartnerRelation;
    } catch (err) {
      logger.error(Logger.format(err));
      const responseError: ResponseError = { errorMessage: 'something went wrong' };
      return rejectWithValue(responseError as ResponseError);
    }
  },
);

export const updateMemberToPartner = createAsyncThunk<
// Return type of the payload creator
MemberPartnerRelation,
// First argument to the payload creator
{ memberId: string, partnerId: string, data: AddMemberToPartnerInput },
// Types for ThunkAPI
{
  rejectValue: ResponseError;
}>(
  'dashboard/updateMemberToPartner',
  async (request, { rejectWithValue }) => {
    const logger = Logger.create('dashboard/updateMemberToPartner');
    try {
      const memberPartnerRelation = await dashboardAPI
        .updateMemberToPartner(request.memberId, request.partnerId, request.data);
      return (memberPartnerRelation || undefined) as MemberPartnerRelation;
    } catch (err) {
      logger.error(Logger.format(err));
      const responseError: ResponseError = { errorMessage: 'something went wrong' };
      return rejectWithValue(responseError as ResponseError);
    }
  },
);

export const getSelectedPartnersForMember = createAsyncThunk<
// Return type of the payload creator
MemberPartnerRelation[],
// First argument to the payload creator
undefined,
// Types for ThunkAPI
{
  rejectValue: ResponseError;
  state: RootState
}>(
  'dashboard/getSelectedPartnersForMember',
  async (_, { rejectWithValue, getState }) => {
    const logger = Logger.create('dashboard/getSelectedPartnersForMember');
    const { memberSession: { memberId } } = getState().onboarding;
    try {
      const response = await dashboardAPI.getSelectedPartnersForMember(memberId);
      return asArrayPayload<MemberPartnerRelation>(
        response ?? [],
        'selected partners for member',
        logger,
      );
    } catch (err) {
      logger.error(Logger.format(err));
      const responseError: ResponseError = { errorMessage: 'failed to get partners for member' };
      return rejectWithValue(responseError as ResponseError);
    }
  },
);

// endpoint: payments/:memberId/year
export const getYearlyPayments = createAsyncThunk<
// Return type of the payload creator
YearlyPayments[],
// First argument to the payload creator
undefined,
// Types for ThunkAPI
{
  rejectValue: ResponseError;
  state: RootState
}>(
  'dashboard/getYearlyPayments',
  async (_, { rejectWithValue, getState }) => {
    const logger = Logger.create('dashboard/getYearlyPayments');
    const { memberSession: { memberId } } = getState().onboarding;
    try {
      const response = await dashboardAPI.getYearlyPaymentsById(memberId);
      return asArrayPayload<YearlyPayments>(response ?? [], 'yearly payments', logger);
    } catch (err) {
      logger.error(Logger.format(err));
      const responseError: ResponseError = { errorMessage: 'failed to get year payments' };
      return rejectWithValue(responseError as ResponseError);
    }
  },
);

export const getMemberConfig = createAsyncThunk<
// Return type of the payload creator
MemberConfig,
// First argument to the payload creator
undefined,
// Types for ThunkAPI
{
  rejectValue: ResponseError;
  state: RootState
}>(
  'dashboard/getMemberConfig',
  async (_, { rejectWithValue, getState }) => {
    const logger = Logger.create('dashboard/getMemberConfig');
    try {
      const { memberSession: { memberId } } = getState().onboarding;
      let memberConfig = await dashboardAPI.getMemberConfig(memberId);
      if (!memberConfig) {
        memberConfig = await dashboardAPI.createMemberConfig({
          showFollowTopicsCard: true,
        }, memberId);
      }
      return memberConfig as MemberConfig;
    } catch (err) {
      logger.error(Logger.format(err));
      const responseError: ResponseError = { errorMessage: 'failed to get member config' };
      return rejectWithValue(responseError as ResponseError);
    }
  },
);

export const getHDSupplyMemberRelation = createAsyncThunk<
// Return type of the payload creator
MemberPartnerRelation,
// First argument to the payload creator
undefined,
// Types for ThunkAPI
{
  rejectValue: ResponseError;
  state: RootState
}>(
  'dashboard/getHDSupplyMemberRelation',
  async (_, { rejectWithValue, getState }) => {
    const logger = Logger.create('dashboard/getHDSupplyMemberRelation');
    const { memberSession: { memberId } } = getState().onboarding;
    const hdSupplyPartnerId = process.env.REACT_APP_HD_SUPPLY_PARTNER_ID || '';
    try {
      const hdSupplyMemberRelation = await dashboardAPI.getHDSupplyMemberRelation(
        memberId,
        hdSupplyPartnerId,
      );
      return (hdSupplyMemberRelation || undefined) as MemberPartnerRelation;
    } catch (err) {
      logger.error(Logger.format(err));
      const responseError: ResponseError = { errorMessage: 'failed to get hdSupplyMemberRelation' };
      return rejectWithValue(responseError as ResponseError);
    }
  },
);

export const verifyAndUpdatePaypalEmail = createAsyncThunk<
// Return type of the payload creator
PayPalAccount,
// First argument to the payload creator
{ memberId: string, data: VerifyMemberPaypalIdentityInput },
// Types for ThunkAPI
{
  rejectValue: ResponseError;
}>(
  'dashboard/verifyAndUpdatePaypalEmail',
  async (request, { rejectWithValue }) => {
    const logger = Logger.create('dashboard/verifyAndUpdatePaypalEmail');
    try {
      logger.debug(Logger.format(request));
      const memberPaypalAccount = await dashboardAPI
        .verifyAndUpdatePaypalEmail(request.memberId, request.data);
      return (memberPaypalAccount || undefined) as PayPalAccount;
    /* eslint-disable @typescript-eslint/no-explicit-any */
    } catch (err: any) {
      logger.error(Logger.format(err));
      let responseError: ResponseError;
      if (err?.status === 400 &&
          err?.response?.data?.message?.includes('is trying to use a payment method that has been used by another company')) {
        responseError = { errorMessage: 'We found an existing account. Please log in or sign up with a different email.' };
      } else {
        responseError = { errorMessage: 'something went wrong' };
      }
      return rejectWithValue(responseError as ResponseError);
    }
  },
);

export const unlinkPaymentMethod = createAsyncThunk<PaymentMethod, { paymentMethodId: string }>(
  'dashboard/unlinkPaymentMethod',
  async (request, { rejectWithValue }) => {
    const logger = Logger.create('dashboard/unlinkPaymentMethod');
    try {
      logger.debug(Logger.format(request));
      const memberPaypalAccount = await dashboardAPI.unlinkPaymentMethod(request.paymentMethodId);
      return (memberPaypalAccount || undefined) as PayPalAccount;
    } catch (err) {
      logger.error(Logger.format(err));
      const responseError: ResponseError = { errorMessage: 'something went wrong while unlinking Paypal account' };
      return rejectWithValue(responseError as ResponseError);
    }
  },
);

export const getCompanyWallet = createAsyncThunk<
// Return type of the payload creator
CompanyWallet,
// First argument to the payload creator
string,
// Types for ThunkAPI
{
  rejectValue: ResponseError;
}>(
  'dashboard/getCompanyWallet',
  async (companyId, { rejectWithValue }) => {
    const logger = Logger.create('dashboard/getCompanyWallet');
    try {
      const companyWallet =
          await dashboardAPI.getCompanyWallet(companyId);
      return companyWallet as CompanyWallet;
    } catch (err) {
      logger.error(Logger.format(err));
      const responseError: ResponseError = { errorMessage: 'failed to get Rewards and Payouts' };
      return rejectWithValue(responseError as ResponseError);
    }
  },
);

export const getCompanyRewardsWallet = createAsyncThunk<
// Return type of the payload creator
CompanyRewardsWallet,
// First argument to the payload creator
string,
// Types for ThunkAPI
{
  rejectValue: ResponseError;
}>(
  'dashboard/getCompanyRewardsWallet',
  async (companyId, { rejectWithValue }) => {
    const logger = Logger.create('dashboard/getCompanyRewardsWallet');
    try {
      const companyWallet =
          await dashboardAPI.getCompanyRewardsWallet(companyId);
      return companyWallet as CompanyRewardsWallet;
    } catch (err) {
      logger.error(Logger.format(err));
      const responseError: ResponseError = { errorMessage: 'failed to get Rewards and Payouts' };
      return rejectWithValue(responseError as ResponseError);
    }
  },
);

export const getCompanyPayoutsWallet = createAsyncThunk<
// Return type of the payload creator
CompanyPayoutsWallet,
// First argument to the payload creator
string,
// Types for ThunkAPI
{
  rejectValue: ResponseError;
}>(
  'dashboard/getCompanyPayoutsWallet',
  async (companyId, { rejectWithValue }) => {
    const logger = Logger.create('dashboard/getCompanyPayoutsWallet');
    try {
      const companyWallet =
          await dashboardAPI.getCompanyPayoutsWallet(companyId);
      return companyWallet as CompanyPayoutsWallet;
    } catch (err) {
      logger.error(Logger.format(err));
      const responseError: ResponseError = { errorMessage: 'failed to get Rewards and Payouts' };
      return rejectWithValue(responseError as ResponseError);
    }
  },
);

export const activatePaymentMethod = createAsyncThunk<
PaymentMethod, { paymentMethodId: string }>(
  'dashboard/activatePaymentMethod',
  async (request, { rejectWithValue }) => {
    const logger = Logger.create('dashboard/activatePaymentMethod');
    try {
      const memberPaymentMethod =
          await dashboardAPI.activatePaymentMethod(request.paymentMethodId);
      return (memberPaymentMethod || undefined) as PaymentMethod;
    } catch (err) {
      logger.error(Logger.format(err));
      const responseError: ResponseError = { errorMessage: 'something went wrong while activating payment method' };
      return rejectWithValue(responseError as ResponseError);
    }
  },
);

export const getPartnerProfileConfig = createAsyncThunk<
// Return type of the payload creator
PartnerProfileConfig,
// First argument to the payload creator
string,
// Types for ThunkAPI
{
  rejectValue: ResponseError;
  state: RootState
}>(
  'dashboard/getPartnerProfileConfig',
  async (partnerId, { rejectWithValue, getState }) => {
    const logger = Logger.create('dashboard/getPartnerProfileConfig');
    try {
      const partnerProfileConfig = await dashboardAPI.getPartnerProfileConfig(
        partnerId,
      );
      return partnerProfileConfig as PartnerProfileConfig;
    } catch (err) {
      logger.error(Logger.format(err));
      const responseError: ResponseError = { errorMessage: 'failed to get PartnerProfileConfig' };
      return rejectWithValue(responseError as ResponseError);
    }
  },
);

export const getAllCategories = createAsyncThunk<
// Return type of the payload creator
Category[],
// First argument to the payload creator
void,
// Types for ThunkAPI
{
  rejectValue: ResponseError;
}>(
  'dashboard/getAllCategories',
  async (_, { rejectWithValue }) => {
    const logger = Logger.create('dashboard/getAllCategories');
    try {
      const categories = await dashboardAPI.getAllCategories();
      return asArrayPayload<Category>(categories, 'categories', logger);
    } catch (err) {
      logger.error(Logger.format(err));
      const responseError: ResponseError = { errorMessage: 'failed to get all categories' };
      return rejectWithValue(responseError as ResponseError);
    }
  },
);

export const getPartnerCategories = createAsyncThunk<
// Return type of the payload creator
Category[],
// First argument to the payload creator
string,
// Types for ThunkAPI
{
  rejectValue: ResponseError;
}>(
  'dashboard/getPartnerCategories',
  async (partnerId, { rejectWithValue }) => {
    const logger = Logger.create('dashboard/getPartnerCategories');
    try {
      const partnerCategories = await dashboardAPI.getPartnerCategories(partnerId);
      return asArrayPayload<Category>(partnerCategories, 'partner categories', logger);
    } catch (err) {
      logger.error(Logger.format(err));
      const responseError: ResponseError = { errorMessage: 'failed to get partner categories' };
      return rejectWithValue(responseError as ResponseError);
    }
  },
);

export interface CompanyPartnerOptin {
  id: string;
  companyId: string;
  partnerId: string;
  optinType: 'Optin' | 'MoreInfo';
}

export const getCompanyPartnerOptin = createAsyncThunk<
CompanyPartnerOptin | undefined,
{ companyId: string; partnerId: string },
{ rejectValue: ResponseError }
>(
  'dashboard/getCompanyPartnerOptin',
  async ({ companyId, partnerId }, { rejectWithValue }) => {
    const logger = Logger.create('dashboard/getCompanyPartnerOptin');
    try {
      const results = await dashboardAPI.getCompanyPartnerOptin(companyId, partnerId);
      return (results as CompanyPartnerOptin[])[0] ?? undefined;
    } catch (err) {
      logger.error(Logger.format(err));
      return rejectWithValue({ errorMessage: 'failed to get company partner optin' });
    }
  },
);

export const createCompanyPartnerOptin = createAsyncThunk<
CompanyPartnerOptin,
{ companyId: string; partnerId: string },
{ rejectValue: ResponseError }
>(
  'dashboard/createCompanyPartnerOptin',
  async ({ companyId, partnerId }, { rejectWithValue }) => {
    const logger = Logger.create('dashboard/createCompanyPartnerOptin');
    try {
      const result = await dashboardAPI.createCompanyPartnerOptin(companyId, partnerId);
      return result as CompanyPartnerOptin;
    } catch (err) {
      logger.error(Logger.format(err));
      return rejectWithValue({ errorMessage: 'failed to create company partner optin' });
    }
  },
);

export const getPartnersByCategories = createAsyncThunk<
// Return type of the payload creator
Partner[],
// First argument to the payload creator
{ categoryIds: string[]; membershipType?: string },
// Types for ThunkAPI
{
  rejectValue: ResponseError;
}>(
  'dashboard/getPartnersByCategories',
  async ({ categoryIds, membershipType }, { rejectWithValue }) => {
    const logger = Logger.create('dashboard/getPartnersByCategories');
    try {
      const partners = await dashboardAPI.getPartnersByCategories(categoryIds, membershipType);
      return asArrayPayload<Partner>(partners, 'partners by categories', logger);
    } catch (err) {
      logger.error(Logger.format(err));
      const responseError: ResponseError = { errorMessage: 'failed to get partners by categories' };
      return rejectWithValue(responseError as ResponseError);
    }
  },
);
